/**
 * Trims leading/trailing whitespace from strings in a request payload.
 *
 * Mirrors the backend's common.trimIncomingValue so what is searched and what is
 * stored match what the user typed. Applying it here as well as on the server
 * keeps the client-side filter functions (most of this app filters its lists in
 * memory) consistent with what the API would have returned.
 *
 * Never mutates binary payloads: FormData, Blob, ArrayBuffer, File and streams
 * are passed through untouched, and Date/Buffer values are left alone.
 */

// Keys whose surrounding whitespace is meaningful — same list as the backend.
const TRIM_EXEMPT_KEYS = new Set([
  "password",
  "pass",
  "old_password",
  "new_password",
  "current_password",
  "confirm_password",
  "confirmpassword",
  "token",
  "authtoken",
  "otp",
  "login_otp",
  "verify_otp",
  "base64",
  "image",
  "photo",
]);

const MAX_DEPTH = 12;

const isBinary = (v) =>
  (typeof FormData !== "undefined" && v instanceof FormData) ||
  (typeof Blob !== "undefined" && v instanceof Blob) ||
  (typeof File !== "undefined" && v instanceof File) ||
  (typeof ArrayBuffer !== "undefined" && v instanceof ArrayBuffer) ||
  ArrayBuffer.isView(v) ||
  (typeof v === "object" && v !== null && typeof v.pipe === "function");

function trimValue(value, key, depth) {
  const level = depth || 0;
  if (level > MAX_DEPTH) return value;

  if (typeof value === "string") return value.trim();

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = trimValue(value[i], key, level + 1);
    }
    return value;
  }

  if (value && typeof value === "object") {
    if (isBinary(value)) return value;
    if (value instanceof Date) return value;
    if (typeof Node !== "undefined" && value instanceof Node) return value;

    for (const k of Object.keys(value)) {
      if (TRIM_EXEMPT_KEYS.has(String(k).toLowerCase())) continue;
      try {
        value[k] = trimValue(value[k], k, level + 1);
      } catch (e) {
        // A getter that throws must not break the request.
      }
    }
    return value;
  }

  return value;
}

/**
 * Trims a value if it is a plain string / object / array.
 * Binary bodies, and raw strings (e.g. a pre-serialised JSON payload) are
 * returned as-is — trimming a JSON string's outer whitespace is pointless and
 * re-serialising would change its exact bytes.
 */
export default function trimRequestPayload(value) {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return value;
  if (isBinary(value)) return value;
  if (typeof value !== "object") return value;
  try {
    return trimValue(value, null, 0);
  } catch (e) {
    return value;
  }
}

export { trimValue, TRIM_EXEMPT_KEYS };
